/**
 * Builds an absolute share URL for a tool route that works in both deploy modes:
 *
 * - **Hash routing** (PR previews / S3+CloudFront): URLs look like
 *   `https://host/preview/branch/index.html#/tools`. The app path lives in
 *   `location.hash` (`#/…`). Share links must keep the same origin + path to
 *   `index.html` and only change the hash (e.g. `#/fleet-optimisation`).
 *
 * - **History routing** (typical production): URLs look like
 *   `https://host/tools` or `https://host/subpath/tools`. Share links must be
 *   normal paths with **no** `#/…` fragment, or the server/router will not load
 *   the right route.
 *
 * Do not use `useLocation().hash` from React Router for this decision — in
 * hash mode the router often exposes pathname only; the real transport is
 * `window.location.hash`.
 */
export function buildToolShareAbsoluteUrl(toolPath: string): string {
  const normalized = toolPath.startsWith('/') ? toolPath : `/${toolPath}`;

  if (typeof window === 'undefined') {
    return normalized;
  }

  const { origin, pathname, search, hash } = window.location;

  if (hash.startsWith('#/')) {
    return `${origin}${pathname}${search}#${normalized}`;
  }

  const pathWithoutLastSegment = pathname.replace(/\/[^/]*$/, '') || '';
  return `${origin}${pathWithoutLastSegment}${normalized}${search}`;
}
